// Identity gate + path inputs for live chat.
//
// The conversation `{key}` is `learnerKey ?? runKey`. `learnerKey` is populated only for
// authenticated learners; teachers/researchers have no valid `{key}` and every chat write is
// rejected by both rule branches. Anonymous PREVIEW runs go offline (disableNetwork()) so the
// trigger never fires — and "preview" is too short for the anonymous rules (`run_key` > 10 chars).
// So live chat is for authenticated learners and real online anonymous runs ONLY.
import { IPortalData, IAnonymousPortalData } from "../../portal-types";

// Owner fields carried on every browser-written chat doc — the subset of the identity fields `answers`
// docs carry that chat scopes/ownership-checks on (snake_case, value-checked against the auth token by
// the Firestore rules). `answers` docs carry additional context fields (e.g. `resource_link_id`) that
// chat does not need.
export interface ChatOwnerFields {
  run_key?: string;
  platform_user_id?: string;
  platform_id?: string;
  context_id?: string;
}

export interface ChatIdentity {
  source: string;              // {source} = database.sourceKey
  key: string;                 // {key} = learnerKey ?? runKey
  ownerFields: ChatOwnerFields;
  activityUrl?: string;        // public activity/sequence URL the function fetches server-side
}

const kMinRunKeyLength = 10; // anonymousCreate() requires run_key > 10 chars

export function getChatIdentity(
  portalData: IPortalData | IAnonymousPortalData | null | undefined
): ChatIdentity | null {
  if (!portalData) return null;

  if (portalData.type === "anonymous") {
    const runKey = portalData.runKey;
    // Exclude preview (offline; "preview" is also too short) and any too-short run key.
    if (!runKey || runKey === "preview" || runKey.length <= kMinRunKeyLength) return null;
    return {
      source: portalData.database.sourceKey,
      key: runKey,
      ownerFields: { run_key: runKey },
      activityUrl: portalData.resourceUrl,
    };
  }

  // authenticated: learners only
  if (portalData.userType !== "learner" || !portalData.learnerKey) return null;
  return {
    source: portalData.database.sourceKey,
    key: portalData.learnerKey,
    ownerFields: {
      platform_user_id: portalData.platformUserId?.toString(),
      platform_id: portalData.platformId,
      context_id: portalData.contextId,
    },
    // resourceUrl, NOT offering.activityUrl. The offering's `activity_url` is the Portal's LAUNCH url
    // (`https://activity-player.concord.org/?activity=<encoded authoring url>`), whose host is not on the
    // function's AUTHORING_HOSTS allowlist, so resolveActivityUrl rejects it with "disallowed activity
    // host" and the turn dies with status:"error". That only shows up when the FIRST turn of a
    // conversation is a typed message: a log-first turn installs the prompt via the trusted default host
    // and never validates this field, which is what masked it in production. resourceUrl is the same
    // getResourceUrl() value the anonymous branch sends, and it resolves to the authoring URL.
    activityUrl: portalData.resourceUrl,
  };
}

// Inputs to the chat mount gate, as plain booleans plus the resolved identity, so the composed
// decision is testable on its own. It lived inline in App.renderActivity, where the individual
// helpers had unit tests but the assembled gate — the thing that actually enforces EXT-2 (no chat on
// single-page activities) and EXT-7 (no chat on the completion page) — had none.
export interface ChatGateInputs {
  chatEnabled: boolean;           // the ?chat / localStorage flag
  idle: boolean;                  // the idle-timeout screen is showing
  hasError: boolean;              // an app-level error screen is showing
  isSinglePageActivity: boolean;  // EXT-2 — a single-page layout has no per-page scoping to chat about
  isContentPage: boolean;         // false on the activity intro page (currentPage === 0)
  isCompletionPage: boolean;      // EXT-7 — the completion page is not a content page to tutor on
  identity: ChatIdentity | null;  // learner/anonymous identity with a usable conversation key
}

export function shouldShowChat(inputs: ChatGateInputs): boolean {
  const {
    chatEnabled, idle, hasError, isSinglePageActivity, isContentPage, isCompletionPage, identity,
  } = inputs;
  return chatEnabled
    && !idle
    && !hasError
    && !isSinglePageActivity
    && isContentPage
    && !isCompletionPage
    && !!identity;
}
